import { Component, computed, DestroyRef, inject, input, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import {
    ApiLicensePlaylistContent,
    InternetMetric,
    LicensesService,
    PlayerInfo,
    PlaylistsService,
} from '@core';
import {
    Button,
    Card,
    GraphComponent,
    GraphConfig,
    GraphVariant,
    Skeleton,
    ThumbnailItem,
    ThumbnailItemComponent,
} from '@ntv360/component-pantry';
import { EmptyComponent, MediaDetailsModalComponent } from '@shared/components';
import { SVG_ICONS } from '@shared/constants';
import { catchError, EMPTY, finalize, forkJoin, map, of, switchMap } from 'rxjs';
import {
    PLAYER_STATUS,
    SCREEN_INFO,
    SYSTEM_INFO,
} from '../../../../constants/player-dummy.constant';

interface StatusItem {
    label: string;
    value: string | number;
}

@Component({
    selector: 'app-details-concept',
    standalone: true,
    imports: [
        Button,
        GraphComponent,
        Card,
        Skeleton,
        ThumbnailItemComponent,
        EmptyComponent,
        MediaDetailsModalComponent,
    ],
    templateUrl: './details-concept.component.html',
    styleUrl: './details-concept.component.scss',
})
export class DetailsConceptComponent implements OnInit {
    /**
     * Injected licenses service.
     */
    private readonly licensesService = inject(LicensesService);

    /**
     * Injected playlists service used to resolve the playlist name shown above the content grid.
     */
    private readonly playlistsService = inject(PlaylistsService);

    /**
     * Injected route.
     */
    private readonly route = inject(ActivatedRoute);

    /**
     * Injected destroy ref.
     */
    private readonly destroyRef = inject(DestroyRef);

    /**
     * Reference to player status data.
     */
    public playerStatus = signal<StatusItem[]>([]);

    /**
     * Reference to screen info data.
     */
    public screenInfo = signal<any>(SCREEN_INFO);

    /**
     * Reference to system info data.
     */
    public systemInfo = signal<StatusItem[]>([]);

    /**
     * Reference to playlist content data.
     */
    public playlistContent = signal<ThumbnailItem[]>([]);

    /** Current playlist contents page (1-based). */
    public playlistPage = signal<number>(1);

    /** Whether more playlist content pages exist. */
    public playlistHasMore = signal<boolean>(false);

    /** Loading state for the Show More action. */
    public isLoadingMorePlaylist = signal<boolean>(false);

    /** Page size for playlist content pagination. */
    private readonly playlistLimit = 10;

    /**
     * Name of the playlist whose contents are shown in the Playlist Content card.
     * Empty string when no playlist is resolved yet.
     */
    public playlistName = signal<string>('');

    /**
     * Identifier of the playlist whose contents are shown in the Playlist Content card.
     * Used to open the single-playlist page when the name is clicked.
     */
    public playlistId = signal<string>('');

    /**
     * The date of the last speedtest performed.
     */
    public lastSpeedtest = signal<string>('N/A');
    /**
     * Controls the visibility of the graph section.
     */
    public showGraph = input<boolean>(false);
    /**
     * Reference to navbar icon constants.
     */
    public icons = SVG_ICONS;

    /**
     * Path to the SVG sprite file.
     */
    public spritePath = 'assets/icons/icons-sprite.svg';

    /**
     * Configuration for the line graph.
     */
    public lineGraphConfig: GraphConfig = {
        variant: GraphVariant.LineWithFilterLegend,
        size: 'custom',
        customSize: {
            width: '100%',
            height: '450px',
        },
        header: 'Training Performance',
        subHeader: 'Overview of training sessions and results',
        series: [
            {
                name: 'Training',
                data: [20, 35, 25, 45, 35, 50, 40],
            },
            {
                name: 'Success',
                data: [15, 32, 20, 40, 32, 45, 38],
            },
            {
                name: 'Failed',
                data: [5, 12, 5, 5, 12, 5, 2],
            },
        ],
        colors: ['#008FFB', '#8DCB2C', '#FF4560'],
        headerMetrics: [
            { label: 'Training', value: 35, color: '#008FFB' },
            { label: 'Success', value: 32, color: '#8DCB2C' },
            { label: 'Failed', value: 12, color: '#FF4560' },
        ],
    };

    /**
     * Configuration for the bar graph.
     */
    public barGraphConfig: GraphConfig = {
        variant: GraphVariant.BarWithFilterLegend,
        size: 'custom',
        customSize: {
            width: '100%',
            height: '450px',
        },
        header: 'Training Performance',
        subHeader: 'Overview of training sessions and results',
        series: [
            {
                name: 'Training',
                data: [20, 35, 25, 45, 35, 50, 40],
            },
            {
                name: 'Success',
                data: [15, 32, 20, 40, 32, 45, 38],
            },
            {
                name: 'Failed',
                data: [5, 12, 5, 5, 12, 5, 2],
            },
        ],
        colors: ['#008FFB', '#8DCB2C', '#FF4560'],
        headerMetrics: [
            { label: 'Training', value: 35, color: '#008FFB' },
            { label: 'Success', value: 32, color: '#8DCB2C' },
            { label: 'Failed', value: 12, color: '#FF4560' },
        ],
    };

    /** Whether the media details modal is visible */
    protected showMediaDetailsModal = signal<boolean>(false);

    /** The currently selected media item for the modal */
    protected selectedItem = signal<ThumbnailItem | null>(null);

    /** The currently selected media item for the modal */
    protected techmagicHide = signal<boolean>(true);

    /** Controls the loading state for the fetched license details. */
    protected isLoading = signal<boolean>(false);

    /** Placeholder rows for the player status card. */
    protected readonly playerStatusSkeletonItems = computed(() =>
        Array.from({ length: this.techmagicHide() ? 7 : 10 }, (_, index) => index),
    );

    /** Placeholder rows for the system info card. */
    protected readonly systemInfoSkeletonItems = Array.from({ length: 13 }, (_, index) => index);

    ngOnInit(): void {
        this.fetchLicenseDetails();
    }

    /**
     * Opens the media details modal for the clicked thumbnail item.
     * @param event - The thumbnail click event containing the item data
     */
    protected onEyeIconClick(item: ThumbnailItem): void {
        this.selectedItem.set(item);
        this.showMediaDetailsModal.set(true);
    }

    /**
     * Fetches all license-related details using forkJoin.
     */
    private fetchLicenseDetails(): void {
        this.isLoading.set(true);

        const id = this.route.snapshot.paramMap.get('id');
        if (!id) {
            this.isLoading.set(false);
            return;
        }

        const emptyPlaylistPage = {
            data: [] as ApiLicensePlaylistContent[],
            meta: { pagination: { hasNextPage: false } },
        };

        forkJoin({
            license: this.licensesService.getLicenseById(id),
            playlistContents: this.licensesService
                .getLicensePlaylistContents(id, { page: 1, limit: this.playlistLimit })
                .pipe(catchError(() => of(emptyPlaylistPage as any))),
            internetMetrics: this.licensesService
                .getInternetMetrics(id)
                .pipe(catchError(() => of(null as InternetMetric | null))),
            playerInfo: this.licensesService
                .getPlayerInfo(id)
                .pipe(catchError(() => of(null as PlayerInfo | null))),
        })
            .pipe(
                catchError((error: unknown) => {
                    console.error(
                        '[LicenseDetailComponent] Error fetching license details:',
                        error,
                    );
                    return EMPTY;
                }),
                finalize(() => this.isLoading.set(false)),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe({
                next: (data: any) => {
                    this.mapDataToUI(data);
                    this.fetchPlaylistName(data.playlistContents);
                },
            });
    }

    /**
     * Resolves the playlist name from the first playlist content item and updates
     * the `playlistName` signal so the Playlist Content card can display it.
     *
     * @param playlistContents - Raw playlist contents payload from the license endpoint.
     */
    private fetchPlaylistName(
        playlistContents:
            ApiLicensePlaylistContent[] | { data?: ApiLicensePlaylistContent[] } | null,
    ): void {
        const items: ApiLicensePlaylistContent[] = Array.isArray(playlistContents)
            ? playlistContents
            : (playlistContents?.data ?? []);
        const playlistId = items[0]?.playlistId;

        if (!playlistId) {
            this.playlistName.set('');
            this.playlistId.set('');
            return;
        }

        this.playlistId.set(playlistId);

        this.playlistsService
            .getPlaylistById(playlistId)
            .pipe(
                map((res) => res?.data?.playlistName ?? ''),
                catchError(() => of('')),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe({
                next: (name) => this.playlistName.set(name),
            });
    }

    /**
     * Opens the single-playlist page for the playlist currently shown in the
     * Playlist Content card.
     */
    protected onPlaylistNameClick(): void {
        const id = this.playlistId();
        if (!id) return;
        window.open(`/playlists/${id}`, '_blank');
    }

    /**
     * Loads the next page of playlist contents and appends to the existing grid.
     */
    protected loadMorePlaylist(): void {
        if (this.isLoadingMorePlaylist() || !this.playlistHasMore()) return;

        const id = this.route.snapshot.paramMap.get('id');
        if (!id) return;

        const nextPage = this.playlistPage() + 1;
        this.isLoadingMorePlaylist.set(true);

        this.licensesService
            .getLicensePlaylistContents(id, { page: nextPage, limit: this.playlistLimit })
            .pipe(
                catchError((error: unknown) => {
                    console.error('[LicenseDetailComponent] Error loading more playlist:', error);
                    return EMPTY;
                }),
                finalize(() => this.isLoadingMorePlaylist.set(false)),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe({
                next: (res) => {
                    const items = this.mapPlaylistContentsToThumbnailItems(res);
                    this.playlistContent.update((current) => [...current, ...items]);
                    this.playlistPage.set(nextPage);
                    this.playlistHasMore.set(!!res?.meta?.pagination?.hasNextPage);
                },
            });
    }

    /**
     * Maps the fetched API response to the component's UI data structures.
     * @param data - The aggregated data from multiple API endpoints
     */
    private mapDataToUI(data: any): void {
        const { internetMetrics, license, playerInfo, playlistContents } = data;
        const status = license?.deviceStatus;
        const deviceInfo = license?.deviceInfo;
        const latestInternet = internetMetrics;
        const player = playerInfo;

        this.playlistContent.set(this.mapPlaylistContentsToThumbnailItems(playlistContents));
        this.playlistPage.set(1);
        this.playlistHasMore.set(!!playlistContents?.meta?.pagination?.hasNextPage);

        // Update Last Speedtest Signal
        this.lastSpeedtest.set(this.formatDate(latestInternet?.dateCreated));

        // Map Player & Pi Status

        // Map System & Connection Info
        if (!this.techmagicHide()) {
            this.systemInfo.set([
                { label: 'Connection Status', value: status?.piStatus ? 'GOOD' : 'BAD' },
                {
                    label: 'Connection Type',
                    value: latestInternet?.internetType?.toUpperCase() || 'N/A',
                },
                {
                    label: 'Download Speed',
                    value: latestInternet?.downloadMbps
                        ? `${Number(latestInternet.downloadMbps)} Mbps`
                        : 'N/A',
                },
                {
                    label: 'Upload Speed',
                    value: latestInternet?.uploadMbps
                        ? `${Number(latestInternet.uploadMbps)} Mbps`
                        : 'N/A',
                },
                {
                    label: 'Storage',
                    value: deviceInfo?.freeStorage
                        ? `${deviceInfo.freeStorage} free of ${deviceInfo.totalStorage}`
                        : 'N/A',
                },
                { label: 'RAM/Memory', value: deviceInfo?.memory || 'N/A' },
                { label: 'Player Version', value: player?.nodeVersion || 'N/A' },
                { label: 'Server Version', value: player?.serverVersion || 'N/A' },
                { label: 'UI Version', value: player?.uiVersion || 'N/A' },
                { label: 'Pi Version', value: deviceInfo?.rpiModel || 'N/A' },
                { label: 'Chromium Version', value: player?.chromiumVersion || 'N/A' },
                { label: 'Current IP', value: deviceInfo?.publicIp || 'N/A' },
                { label: 'Previous IP', value: deviceInfo?.prevPublicIp || 'N/A' },
                { label: 'Mac Address', value: deviceInfo?.macAddress || 'N/A' },
            ]);
            this.playerStatus.set([
                { label: 'Pi Status', value: status?.piStatus ? 'ONLINE' : 'OFFLINE' },
                { label: 'Player Status', value: status?.playerStatus ? 'ONLINE' : 'OFFLINE' },
                { label: 'TV Status', value: status?.tvPowerStatus ? 'ONLINE' : 'OFFLINE' },
                { label: 'Display Status', value: status?.displayStatus ? 'ONLINE' : 'OFFLINE' },
                { label: 'Last Startup', value: this.formatDate(status?.updatedAt) },
                { label: 'Last Disconnect', value: 'N/A' },
                { label: 'Last Update', value: this.formatDate(status?.updatedAt) },
                { label: 'TV Brand', value: deviceInfo?.tvBrand || 'N/A' },
                { label: 'Anydesk', value: deviceInfo?.anydeskId || 'N/A' },
                { label: 'Anydesk Password', value: license?.anyDesk?.password || 'N/A' },
            ]);
        } else {
            this.systemInfo.set([
                { label: 'Connection Status', value: status?.piStatus ? 'GOOD' : 'BAD' },
                {
                    label: 'Connection Type',
                    value: latestInternet?.internetType?.toUpperCase() || 'N/A',
                },
                {
                    label: 'Download Speed',
                    value: latestInternet?.downloadMbps
                        ? `${Number(latestInternet.downloadMbps)} Mbps`
                        : 'N/A',
                },
                {
                    label: 'Upload Speed',
                    value: latestInternet?.uploadMbps
                        ? `${Number(latestInternet.uploadMbps)} Mbps`
                        : 'N/A',
                },
                {
                    label: 'Storage',
                    value: deviceInfo?.freeStorage
                        ? `${deviceInfo.freeStorage} free of ${deviceInfo.totalStorage}`
                        : 'N/A',
                },
                { label: 'RAM/Memory', value: deviceInfo?.memory || 'N/A' },
                { label: 'Player Version', value: player?.nodeVersion || 'N/A' },
                { label: 'Server Version', value: player?.serverVersion || 'N/A' },
                { label: 'UI Version', value: player?.uiVersion || 'N/A' },
                { label: 'Pi Version', value: deviceInfo?.rpiModel || 'N/A' },
                { label: 'Current IP', value: deviceInfo?.publicIp || 'N/A' },
                { label: 'Previous IP', value: deviceInfo?.prevPublicIp || 'N/A' },
                { label: 'Mac Address', value: deviceInfo?.macAddress || 'N/A' },
            ]);

            this.playerStatus.set([
                { label: 'Pi Status', value: status?.piStatus ? 'ONLINE' : 'OFFLINE' },
                { label: 'Player Status', value: status?.playerStatus ? 'ONLINE' : 'OFFLINE' },
                { label: 'TV Status', value: status?.tvPowerStatus ? 'ONLINE' : 'OFFLINE' },
                { label: 'Display Status', value: status?.displayStatus ? 'ONLINE' : 'OFFLINE' },
                { label: 'Last Startup', value: this.formatDate(playerInfo?.currentStartup) },
                { label: 'TV Brand', value: deviceInfo?.tvBrand || 'N/A' },
                { label: 'Anydesk', value: deviceInfo?.anydeskId || 'N/A' },
                { label: 'Anydesk Password', value: license?.anyDesk?.password || 'N/A' },
            ]);
        }

        // Map Screen & Host Info
        if (license && license.hostLocation) {
            this.screenInfo.update((current) => ({
                ...current,
                screenName: license.hostLocation.businessName || current.screenName,
            }));
        }
    }

    /**
     * Converts playlist content API responses into thumbnail items for the grid.
     *
     * @param playlistContents - Raw playlist content payload from the API
     * @returns Array of thumbnail items consumable by the component pantry
     */
    private mapPlaylistContentsToThumbnailItems(
        playlistContents:
            ApiLicensePlaylistContent[] | { data?: ApiLicensePlaylistContent[] } | null,
    ): ThumbnailItem[] {
        const items: ApiLicensePlaylistContent[] = Array.isArray(playlistContents)
            ? playlistContents
            : (playlistContents?.data ?? []);

        return items.map((item: ApiLicensePlaylistContent, index: number) => {
            const media = item.content;
            const url = media?.url ?? '';
            const thumbnail = media?.thumbnail ?? '';
            const fileType = media?.file_type ?? media?.fileType ?? '';
            const type = this.resolveThumbnailType(fileType);

            return {
                id: `${item.playlistContentId ?? item.contentId ?? 'item'}-${index}`,
                name: media?.title ?? media?.fileName ?? media?.file_name ?? 'Untitled',
                type,
                // Modal reads `src` for both <video> and <img>. Use url (playable file)
                // when available; thumbnail otherwise (so images still render).
                src: url || thumbnail,
                thumbnail: thumbnail || url,
                url,
                duration: media?.duration != null ? String(media.duration) : undefined,
            } as ThumbnailItem;
        });
    }

    /**
     * Resolves a raw MIME type string to a supported ThumbnailItem type.
     *
     * @param fileType - Raw MIME type from the API (e.g. "image/png")
     * @returns Normalized thumbnail type literal
     */
    private resolveThumbnailType(fileType: string | undefined | null): ThumbnailItem['type'] {
        if (!fileType) return 'unknown';
        const prefix = fileType.split('/')[0];
        switch (prefix) {
            case 'image':
                return 'image';
            case 'video':
                return 'video';
            case 'audio':
                return 'audio';
            default:
                return 'unknown';
        }
    }

    /**
     * Formats a date string or object into a human-readable format.
     * @param date - The date value to format
     */
    private formatDate(date: any): string {
        if (!date) return 'N/A';
        return new Date(date).toLocaleString('en-US', {
            month: 'short',
            day: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true,
        });
    }
}
